import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, FileText, X, Lock, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import api from '../utils/api';

function ProgressBar({ progress }) {
  return (
    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
      <div
        className="h-2 rounded-full bg-sky-500 transition-all duration-1000 ease-in-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

function FileItem({ file, status, progress, onRemove }) {
  const icons = {
    pending:  <FileText className="w-5 h-5 text-slate-400" />,
    uploading: <Loader className="w-5 h-5 text-sky-500 animate-spin" />,
    processing: <Loader className="w-5 h-5 text-amber-500 animate-spin" />,
    complete: <CheckCircle className="w-5 h-5 text-emerald-500" />,
    error:    <AlertCircle className="w-5 h-5 text-red-500" />,
    duplicate: <AlertCircle className="w-5 h-5 text-amber-500" />,
    password_required: <Lock className="w-5 h-5 text-amber-500" />,
  };

  const statusLabel = {
    pending: 'Ready to upload',
    uploading: 'Uploading…',
    processing: 'Processing…',
    complete: 'Complete',
    error: 'Error',
    duplicate: 'Already uploaded',
    password_required: 'Password required',
  };

  const statusColor = {
    complete: 'text-emerald-600',
    error: 'text-red-600',
    duplicate: 'text-amber-600',
    password_required: 'text-amber-600',
    processing: 'text-amber-600',
  };

  return (
    <div className="flex items-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
      {icons[status] || icons.pending}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{file.name}</p>
        <div className="flex items-center gap-2 mt-1">
          <p className={`text-xs ${statusColor[status] || 'text-slate-500'}`}>{statusLabel[status] || status}</p>
          <span className="text-xs text-slate-400">·</span>
          <p className="text-xs text-slate-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
        </div>
        {(status === 'processing' || status === 'uploading') && (
          <div className="mt-2">
            <ProgressBar progress={progress || 10} />
          </div>
        )}
      </div>
      {status === 'pending' && (
        <button onClick={onRemove} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function PasswordModal({ filename, statementId, onUnlocked, onClose }) {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true);
    try {
      const r = await api.post(`/statements/${statementId}/unlock`, { password: pwd });
      const txnCount = r.data?.transaction_count || 0;
      toast.success(`PDF unlocked — ${txnCount} transactions extracted.`);
      onUnlocked(statementId);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wrong password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open title="PDF Password Required" onClose={onClose}
      description={`"${filename}" is password-protected. Enter the password to continue.`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            placeholder="Bank statement password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            autoFocus
            className="w-full h-9 px-3 pr-16 rounded-md border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-700">
            {show ? 'Hide' : 'Show'}
          </button>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">Typical format: last 4 digits of account number, or date of birth (DDMMYYYY).</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
          <Button type="submit" loading={loading}>{loading ? 'Unlocking…' : 'Unlock & Process'}</Button>
        </div>
      </form>
    </Dialog>
  );
}

export default function UploadPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [fileStatuses, setFileStatuses] = useState({});
  const [fileProgress, setFileProgress] = useState({});
  const [statementIds, setStatementIds] = useState({});
  const [pwdModal, setPwdModal] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const pollRefs = useRef({});
  const progressRefs = useRef({});

  const addFiles = (incoming) => {
    const pdfs = Array.from(incoming).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) { toast.error('Please select PDF files only.'); return; }
    if (files.length + pdfs.length > 10) { toast.error('Maximum 10 files at once.'); return; }
    setFiles(prev => [...prev, ...pdfs]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const simulateProgress = (filename) => {
    // Grows from 8% → 88% over 3 minutes then holds
    let pct = 8;
    const interval = setInterval(() => {
      pct = Math.min(88, pct + (Math.random() * 1.5));
      setFileProgress(p => ({ ...p, [filename]: Math.round(pct) }));
    }, 3000);
    progressRefs.current[filename] = interval;
  };

  const pollStatus = (stmtId, filename) => {
    const poll = setInterval(async () => {
      try {
        const r = await api.get(`/statements/${stmtId}/status`);
        const { status } = r.data;
        if (status === 'complete') {
          clearInterval(pollRefs.current[stmtId]);
          clearInterval(progressRefs.current[filename]);
          setFileStatuses(s => ({ ...s, [filename]: 'complete' }));
          setFileProgress(p => ({ ...p, [filename]: 100 }));
          toast.success(`"${filename}" processed successfully! Redirecting…`);
          // Navigate straight to this statement's transactions after a brief pause
          setTimeout(() => {
            const totalFiles = files.length;
            const completedCount = Object.values({ ...fileStatuses, [filename]: 'complete' })
              .filter(st => st === 'complete').length;
            if (totalFiles === 1 || completedCount === totalFiles) {
              navigate(`/statements/${stmtId}`);
            }
          }, 1500);
        } else if (status === 'error') {
          clearInterval(pollRefs.current[stmtId]);
          clearInterval(progressRefs.current[filename]);
          setFileStatuses(s => ({ ...s, [filename]: 'error' }));
          const errMsg = r.data.error_message || 'Unknown error';
          toast.error(errMsg, { duration: 10000 });
        } else if (status === 'password_required') {
          clearInterval(pollRefs.current[stmtId]);
          clearInterval(progressRefs.current[filename]);
          setFileStatuses(s => ({ ...s, [filename]: 'password_required' }));
          setPwdModal({ statementId: stmtId, filename });
        }
      } catch { /* ignore */ }
    }, 2000);
    pollRefs.current[stmtId] = poll;
  };

  const handleUpload = async () => {
    const pending = files.filter(f => !['complete', 'error', 'duplicate', 'password_required'].includes(fileStatuses[f.name]));
    if (!pending.length) return;

    setUploading(true);
    // Mark all pending as uploading then switch to processing (server processes synchronously)
    pending.forEach(f => setFileStatuses(s => ({ ...s, [f.name]: 'uploading' })));

    const formData = new FormData();
    pending.forEach(f => formData.append('files', f));

    // Switch to "processing" state after a short delay to show the progress bar
    const processingTimer = setTimeout(() => {
      pending.forEach(f => setFileStatuses(s => {
        if (s[f.name] === 'uploading') return { ...s, [f.name]: 'processing' };
        return s;
      }));
      pending.forEach(f => simulateProgress(f.name));
    }, 1500);

    try {
      // No per-request timeout override — uses api.js global (5 min)
      const r = await api.post('/statements/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      clearTimeout(processingTimer);
      const results = r.data.results || [];
      let firstCompleteId = null;
      let completeCount = 0;

      results.forEach((res) => {
        const fn = res.filename;
        clearInterval(progressRefs.current[fn]);

        if (res.status === 'complete') {
          // Synchronous processing — already done!
          setFileStatuses(s => ({ ...s, [fn]: 'complete' }));
          setFileProgress(p => ({ ...p, [fn]: 100 }));
          setStatementIds(ids => ({ ...ids, [fn]: res.statement_id }));
          if (!firstCompleteId) firstCompleteId = res.statement_id;
          completeCount++;
          toast.success(`"${fn}" processed — ${res.transaction_count || 0} transactions found.`);
        } else if (res.status === 'duplicate') {
          setFileStatuses(s => ({ ...s, [fn]: 'duplicate' }));
          toast.warning(res.message);
        } else if (res.status === 'password_required') {
          setFileStatuses(s => ({ ...s, [fn]: 'password_required' }));
          setStatementIds(ids => ({ ...ids, [fn]: res.statement_id }));
          setPwdModal({ statementId: res.statement_id, filename: fn });
        } else if (res.status === 'processing') {
          // Fallback: server returned async (shouldn't happen on Vercel but handle it)
          setFileStatuses(s => ({ ...s, [fn]: 'processing' }));
          setStatementIds(ids => ({ ...ids, [fn]: res.statement_id }));
          simulateProgress(fn);
          pollStatus(res.statement_id, fn);
        } else if (res.status === 'error') {
          setFileStatuses(s => ({ ...s, [fn]: 'error' }));
          toast.error(res.message || 'Processing failed. Please try again.', { duration: 10000 });
        }
      });

      // Auto-navigate: single file → review page; multiple → dashboard
      if (firstCompleteId && completeCount === results.filter(r => r.status !== 'password_required').length) {
        setTimeout(() => {
          if (results.length === 1) {
            navigate(`/review/${firstCompleteId}`);
          } else {
            navigate('/dashboard');
          }
        }, 1500);
      }
    } catch (err) {
      clearTimeout(processingTimer);
      const errDetail = err.response?.data?.detail || err.message || 'Unknown error';
      const isTimeout = err.code === 'ECONNABORTED' || errDetail.toLowerCase().includes('timeout');
      const isNetwork = err.code === 'ERR_NETWORK' || errDetail.toLowerCase().includes('network');
      let userMsg = `Upload failed: ${errDetail}`;
      if (isTimeout) userMsg = 'The server took too long. Please try again — large PDFs can take up to 2 minutes.';
      else if (isNetwork) userMsg = 'Could not reach the server. Check your connection and try again.';
      toast.error(userMsg, { duration: 10000 });
      pending.forEach(f => setFileStatuses(s => ({ ...s, [f.name]: 'error' })));
    } finally {
      setUploading(false);
    }
  };

  const handleUnlocked = (stmtId) => {
    // Unlock is now synchronous — backend already processed by the time the modal closes.
    // Mark complete and navigate.
    const fn = Object.entries(statementIds).find(([, id]) => id === stmtId)?.[0];
    if (fn) {
      setFileStatuses(s => ({ ...s, [fn]: 'complete' }));
      setFileProgress(p => ({ ...p, [fn]: 100 }));
      setTimeout(() => navigate(`/review/${stmtId}`), 1000);
    }
  };

  useEffect(() => {
    const polls = pollRefs.current;
    const progresses = progressRefs.current;
    return () => {
      Object.values(polls).forEach(clearInterval);
      Object.values(progresses).forEach(clearInterval);
    };
  }, []);

  const allDone = files.length > 0 && files.every(f => ['complete', 'error', 'duplicate', 'password_required'].includes(fileStatuses[f.name]));
  const anyComplete = files.some(f => fileStatuses[f.name] === 'complete');
  const hasActive = files.some(f => ['processing', 'uploading'].includes(fileStatuses[f.name]));

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Upload Bank Statements</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Upload up to 10 PDF files at once. Password-protected PDFs are supported.</p>
        </div>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors cursor-pointer
            ${dragOver ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/10' : 'border-slate-300 dark:border-slate-600 hover:border-sky-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
        >
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-colors ${dragOver ? 'bg-sky-100 dark:bg-sky-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
            <Upload className={`w-8 h-8 ${dragOver ? 'text-sky-600' : 'text-slate-400'}`} />
          </div>
          <p className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">
            {dragOver ? 'Drop PDFs here' : 'Drag & drop PDFs here'}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">or click to browse files</p>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-200 dark:bg-slate-700 text-xs text-slate-600 dark:text-slate-400">
            <FileText className="w-3.5 h-3.5" />
            PDF only · Max 10 files · Password PDFs supported
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf"
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-6 space-y-3">
            {files.map((f, i) => (
              <FileItem
                key={`${f.name}-${i}`}
                file={f}
                status={fileStatuses[f.name] || 'pending'}
                progress={fileProgress[f.name] || 0}
                onRemove={() => removeFile(i)}
              />
            ))}
          </div>
        )}

        {/* Processing notice */}
        {hasActive && (
          <div className="mt-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">⏱ Uploading & processing your statement…</p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
              This usually takes 5–30 seconds. You'll be taken directly to your results when done.<br />
              <strong>Do not close this tab.</strong>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex items-center gap-3">
          {!allDone && files.length > 0 && (
            <Button
              onClick={handleUpload}
              loading={uploading || hasActive}
              disabled={uploading || hasActive || files.length === 0}
              size="lg"
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading…' : hasActive ? 'Processing…' : `Upload ${files.length} File${files.length > 1 ? 's' : ''}`}
            </Button>
          )}
          {anyComplete && (
            <>
              {files.length === 1 && statementIds[files[0]?.name] ? (
                <Button variant="outline" onClick={() => navigate(`/statements/${statementIds[files[0].name]}`)} size="lg">
                  View Transactions
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/dashboard')} size="lg">
                  View Dashboard
                </Button>
              )}
            </>
          )}
          {allDone && anyComplete && (
            <p className="text-sm text-emerald-600 dark:text-emerald-400">✓ All files processed successfully</p>
          )}
        </div>
      </div>

      {/* Password modal */}
      {pwdModal && (
        <PasswordModal
          filename={pwdModal.filename}
          statementId={pwdModal.statementId}
          onUnlocked={handleUnlocked}
          onClose={() => setPwdModal(null)}
        />
      )}
    </DashboardLayout>
  );
}
